import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'

export const MORE_LABEL = '… More'
export const LESS_LABEL = ' Less'

type Props = {
  reason: string
  reservedRight?: number
}

type WidthOf = (value: string, withSuffix: boolean) => number

function cutToWidth(reason: string, available: number, widthOf: WidthOf) {
  let lo = 0
  let hi = reason.length

  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2)
    if (widthOf(reason.slice(0, mid), true) <= available) {
      lo = mid
    } else {
      hi = mid - 1
    }
  }

  return reason.slice(0, lo).trimEnd()
}

export function ExpandableReason(props: Props) {
  const { reason, reservedRight = 0 } = props

  const [expanded, setExpanded] = useState(false)
  const [truncated, setTruncated] = useState<string | null>(null)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const measureTextRef = useRef<HTMLSpanElement | null>(null)
  const measureSuffixRef = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    setExpanded(false)
  }, [reason])

  const measure = useCallback(() => {
    const container = containerRef.current
    const measurer = measureRef.current
    const textNode = measureTextRef.current
    const suffixNode = measureSuffixRef.current
    if (!container || !measurer || !textNode || !suffixNode) return

    const available = container.clientWidth - reservedRight

    const widthOf: WidthOf = (value, withSuffix) => {
      textNode.textContent = value
      suffixNode.textContent = withSuffix ? MORE_LABEL : ''
      return measurer.scrollWidth
    }

    const next =
      available > 0 && widthOf(reason, false) > available
        ? cutToWidth(reason, available, widthOf)
        : null

    setTruncated((current) => (current === next ? current : next))
  }, [reason, reservedRight])

  useLayoutEffect(() => {
    measure()

    const container = containerRef.current
    if (!container || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(measure)
    observer.observe(container)

    return () => observer.disconnect()
  }, [measure])

  const handleToggle = useCallback(() => {
    setExpanded((value) => !value)
  }, [])

  const canToggle = truncated !== null
  const visibleReason = !canToggle || expanded ? reason : truncated

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <Typography
        variant="body2"
        data-test-id="knock-status-reason"
        sx={{
          mt: 0.4,
          mr: reservedRight ? `${reservedRight}px` : 0,
          overflowWrap: 'anywhere',
        }}
      >
        <Typography component="span" variant="subtitle4">
          Reason:
        </Typography>{' '}
        {visibleReason}
        {canToggle && (
          <Link
            component="button"
            type="button"
            onClick={handleToggle}
            data-test-id="expandable-reason-toggle"
            sx={{
              font: 'inherit',
              color: '#1565C0',
              verticalAlign: 'baseline',
              border: 0,
              p: 0,
              background: 'none',
              cursor: 'pointer',
              whiteSpace: 'pre',
            }}
          >
            {expanded ? LESS_LABEL : MORE_LABEL}
          </Link>
        )}
      </Typography>

      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          overflow: 'hidden',
        }}
      >
        <Typography
          ref={measureRef}
          component="div"
          variant="body2"
          sx={{ whiteSpace: 'pre', width: 'max-content' }}
        >
          <Typography component="span" variant="subtitle4">
            Reason:
          </Typography>{' '}
          <span ref={measureTextRef} />
          <span ref={measureSuffixRef} />
        </Typography>
      </Box>
    </Box>
  )
}
